import { useTranslation } from 'react-i18next';
import {
  Datatable,
  DatatableBody,
  DatatableCell,
  DatatableHead,
  DatatableHeader,
  DatatableRow,
  Skeleton,
} from '@monksflow/monks-ui';
import { TableSkeletonActionsCell, TableSkeletonRow } from '@/shared/ui/data-table';
import { AccountRow } from '../AccountRow';
import type { AccountsTableProps } from './AccountsTable.types';

const SKELETON_ROWS = Array.from({ length: 5 }, (_, i) => i);

export function AccountsTable({
  accounts,
  syncStatusMap,
  isBusy,
  onEdit,
  onDelete,
  onToggle,
  onSync,
  onTest,
  testingAccountId,
  togglingAccountId,
  syncingAccountId,
}: AccountsTableProps) {
  const { t } = useTranslation('knowledge');

  return (
    <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">
      <Datatable aria-busy={isBusy || undefined} className="[&_thead_th]:border-b">
        <DatatableHeader>
          <DatatableRow>
            <DatatableHead scope="col" className="w-[calc(100%_-_50rem)]">
              {t('mediaConnectors.mediaConnectorDetail.account')}
            </DatatableHead>
            <DatatableHead scope="col" className="w-40">
              {t('mediaConnectors.mediaConnectorDetail.accountId')}
            </DatatableHead>
            <DatatableHead scope="col" className="w-[15rem]">
              {t('mediaConnectors.mediaConnectorDetail.tags')}
            </DatatableHead>
            <DatatableHead scope="col" className="w-32">
              {t('mediaConnectors.mediaConnectorDetail.status')}
            </DatatableHead>
            <DatatableHead scope="col" className="w-48">
              {t('mediaConnectors.mediaConnectorDetail.capabilities')}
            </DatatableHead>
            <DatatableHead scope="col" className="w-36">
              {t('mediaConnectors.mediaConnectorDetail.lastSync')}
            </DatatableHead>
            <DatatableHead scope="col" className="w-32">
              {t('mediaConnectors.mediaConnectorDetail.syncFreq')}
            </DatatableHead>
            <DatatableHead scope="col" className="w-20">
              {t('mediaConnectors.mediaConnectorDetail.enabled')}
            </DatatableHead>
            <DatatableHead scope="col" className="w-13">
              <span className="sr-only">{t('mediaConnectors.mediaConnectorDetail.actions')}</span>
            </DatatableHead>
          </DatatableRow>
        </DatatableHeader>
        <DatatableBody>
          {isBusy ? (
            SKELETON_ROWS.map((i) => (
              <TableSkeletonRow key={i} testId="connector-table-skeleton-row">
                <DatatableCell>
                  <Skeleton className="h-5 w-48" />
                </DatatableCell>
                <DatatableCell>
                  <Skeleton className="h-5 w-28" />
                </DatatableCell>
                <DatatableCell>
                  <div className="flex gap-1">
                    <Skeleton className="h-6 w-16 rounded-full" />
                    <Skeleton className="h-6 w-14 rounded-full" />
                  </div>
                </DatatableCell>
                <DatatableCell>
                  <Skeleton className="h-6 w-20 rounded-full" />
                </DatatableCell>
                <DatatableCell>
                  <Skeleton className="h-5 w-24" />
                </DatatableCell>
                <DatatableCell>
                  <Skeleton className="h-5 w-20" />
                </DatatableCell>
                <DatatableCell>
                  <Skeleton className="h-5 w-10 rounded-full" />
                </DatatableCell>
                <TableSkeletonActionsCell />
              </TableSkeletonRow>
            ))
          ) : accounts.length === 0 ? (
            <DatatableRow>
              <DatatableCell colSpan={8}>
                <p className="py-8 text-center text-sm text-foreground-alt">
                  {t('mediaConnectors.mediaConnectorDetail.noAccountsMatchYourFilters')}
                </p>
              </DatatableCell>
            </DatatableRow>
          ) : (
            accounts.map((account) => (
              <AccountRow
                key={account.id}
                account={account}
                syncStatus={syncStatusMap.get(account.id)}
                onEdit={() => onEdit(account)}
                onDelete={() => onDelete(account)}
                onToggle={(checked) => onToggle(account, checked)}
                onSync={() => onSync(account.id)}
                onTest={() => onTest(account.id)}
                isTesting={testingAccountId === account.id}
                isToggling={togglingAccountId === account.id}
                isSyncing={syncingAccountId === account.id}
              />
            ))
          )}
        </DatatableBody>
      </Datatable>
    </div>
  );
}

AccountsTable.displayName = 'AccountsTable';
